import type { NotificationRouteAlert } from '@savvy/constants/generated/gql-enums';

export type NotificationRoutingTune =
  | 'transfer'
  | 'cash'
  | 'allocation'
  | 'weeks'
  | 'birthday'
  | 'days'
  | 'docusignDays';

export type NotificationRoutingRow = {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly alertTypes: readonly NotificationRouteAlert[];
  readonly tune?: NotificationRoutingTune;
  // Activity-only event: no alert producer, so it can only land in the feed (Inbox is not offered).
  readonly feedOnly?: boolean;
  // Optional subgroup header rendered before the first row of each distinct group within a section.
  readonly group?: string;
  // Inbox-only alert: the advisor activity feed is household-keyed on both ends, so a type that
  // resolves no household records nothing there (Activity is not offered).
  readonly inboxOnly?: boolean;
  // Like inboxOnly (no Activity row exists, so the Activity column is a dash) but the Inbox toggle
  // stays enabled so the advisor can turn it off — toggling off routes to Activity, which for this
  // type is nowhere, i.e. no notification at all (UNO-1049).
  readonly inboxOptional?: boolean;
};

export type NotificationRoutingSection = {
  readonly category: string;
  readonly title: string;
  readonly rows: readonly NotificationRoutingRow[];
};

export const NOTIFICATION_ROUTING_SECTIONS: readonly NotificationRoutingSection[] = [
  {
    category: 'more-task-activity',
    title: 'More task activity',
    rows: [
      {
        id: 'task-waiting-advisor',
        label: 'Task waiting on advisor input',
        description: 'A task moved to "Pending Advisor" and needs someone on the team.',
        alertTypes: ['advisor_requirements_needed'],
      },
      {
        id: 'client-completed-task',
        label: 'Client completed a task',
        description: 'A client finished a task you assigned to them.',
        alertTypes: ['client_completed_task'],
      },
      {
        id: 'task-reassigned',
        label: 'Task reassigned',
        description: 'A task was handed off to someone else.',
        alertTypes: ['task_reassigned_notification'],
      },
      {
        id: 'task-created',
        label: 'New task created',
        description: 'A task was created on a household you work with.',
        alertTypes: ['task_created'],
      },
      {
        id: 'task-due-date-changed',
        label: 'Task due date changed',
        description: "Someone changed a task's due date.",
        alertTypes: ['task_due_date_changed'],
      },
      {
        id: 'docusign-outstanding',
        label: 'DocuSign outstanding',
        description: 'A DocuSign envelope has gone unsigned past your threshold.',
        alertTypes: ['docusign_outstanding'],
        tune: 'docusignDays',
      },
    ],
  },
  {
    category: 'client-onboarding',
    title: 'Client Onboarding',
    rows: [
      {
        id: 'intake-invite-sent',
        label: 'Intake invite sent',
        description: 'An intake invite went out to a client.',
        alertTypes: ['intake_invite_sent'],
      },
      {
        id: 'intake-reminder-sent',
        label: 'Intake reminder sent',
        description: 'A reminder to complete intake went out.',
        alertTypes: ['intake_invite_reminder_sent'],
      },
      {
        id: 'onboarding-invite-sent',
        label: 'Onboarding invite sent',
        description: 'An onboarding invite went out to a client.',
        alertTypes: ['onboarding_invite_sent'],
      },
      {
        id: 'onboarding-reminder-sent',
        label: 'Onboarding reminder sent',
        description: 'A reminder to finish onboarding went out.',
        alertTypes: ['onboarding_invite_reminder_sent'],
      },
      {
        id: 'account-setup-email-sent',
        label: 'Account setup email sent',
        description: 'An account setup or confirmation email went out.',
        alertTypes: ['account_setup_confirmation_sent'],
      },
      {
        id: 'file-request-sent',
        label: 'File upload request sent',
        description: 'A request for documents went out to a client.',
        alertTypes: ['file_upload_request_sent'],
      },
      {
        id: 'risk-questionnaire-sent',
        label: 'Risk questionnaire sent',
        description: 'A risk questionnaire went out to a client.',
        alertTypes: ['risk_questionnaire_sent'],
      },
      {
        id: 'prereview-checkin-sent',
        label: 'Pre-review check-in sent',
        description: 'A pre-review check-in went out to a client.',
        alertTypes: ['pre_review_check_in_sent'],
      },
      {
        id: 'client-uploaded-file',
        label: 'Client uploaded a file',
        description: 'A client uploaded a document you requested.',
        alertTypes: ['client_uploaded_requested_file'],
      },
      {
        id: 'risk-questionnaire-submitted',
        label: 'Client submitted risk questionnaire',
        description: 'A client completed their risk questionnaire.',
        alertTypes: ['client_submitted_risk_questionnaire'],
      },
      {
        id: 'onboarding-complete',
        label: 'Onboarding complete',
        description: 'A household finished onboarding and is now active.',
        alertTypes: ['onboarding_complete'],
      },
    ],
  },
  {
    category: 'service-calendar',
    title: 'Service Calendar',
    rows: [
      {
        id: 'sc-invite-sent',
        label: 'Service calendar invite sent',
        description: 'A scheduling invite went out to a client.',
        alertTypes: ['service_calendar_invite_sent'],
      },
      {
        id: 'sc-booking-reminder',
        label: 'Booking reminder sent',
        description: 'A reminder to book went out to a client.',
        alertTypes: ['service_calendar_booking_reminder_sent'],
      },
      {
        id: 'sc-meeting-booked',
        label: 'Meeting booked by client',
        description: 'A client booked a meeting with you.',
        alertTypes: ['meeting_booked_by_client'],
      },
      {
        id: 'sc-meeting-changed',
        label: 'Meeting canceled or rescheduled',
        description: 'A client canceled or moved a meeting.',
        alertTypes: ['meeting_canceled_or_rescheduled_by_client'],
      },
      {
        id: 'sc-task-assigned',
        label: 'Service calendar task assigned',
        description: 'A service task was assigned to you.',
        alertTypes: ['service_calendar_task_assigned'],
      },
    ],
  },
  {
    category: 'leads-prospects',
    title: 'Leads & Prospects',
    rows: [
      {
        id: 'lead-campaign-started',
        label: 'New lead campaign started',
        description: 'A campaign kicked off for a lead.',
        alertTypes: ['new_lead_campaign_started'],
      },
      {
        id: 'new-lead-created',
        label: 'New lead created',
        description: 'A new lead came in.',
        alertTypes: ['new_lead_created'],
      },
      {
        id: 'email-from-lead',
        label: 'Email received from lead',
        description: 'A lead emailed you.',
        alertTypes: ['email_from_lead'],
      },
      {
        id: 'text-from-lead',
        label: 'Text received from lead',
        description: 'A lead texted you.',
        alertTypes: ['sms_from_lead'],
      },
      {
        id: 'missed-call-from-lead',
        label: 'Missed call from lead',
        description: 'A lead called and you missed it.',
        alertTypes: ['inbound_missed_call'],
      },
      {
        id: 'outbound-call-reminder',
        label: 'Outbound call reminder',
        description: 'A reminder to call a lead back.',
        alertTypes: ['outbound_missed_call_reminder'],
      },
      {
        id: 'stagnant-prospect',
        label: 'Stagnant prospect',
        description: "A prospect hasn't moved forward in a while.",
        alertTypes: ['stagnant_prospect'],
      },
      {
        id: 'prospect-converted',
        label: 'Prospect converted to client',
        description: 'A prospect became a client.',
        alertTypes: ['prospect_converted'],
      },
    ],
  },
  {
    category: 'tax',
    title: 'Tax',
    rows: [
      {
        id: 'tax-filing-sent-client',
        label: 'Tax filing ready (sent to client)',
        description: 'A filing was sent to a client to review.',
        alertTypes: ['tax_filing_ready_sent_to_client'],
      },
      {
        id: 'tax-filing-reminder',
        label: 'Tax filing reminder sent',
        description: 'A reminder to file went out to a client.',
        alertTypes: ['tax_filing_reminder_to_client'],
      },
      {
        id: 'tax-late-filing',
        label: 'Late filing or missed deadline',
        description: 'A client missed a tax deadline.',
        alertTypes: ['tax_filing_late_or_missed_deadline'],
      },
      {
        id: 'tax-federal-update',
        label: 'Federal return update',
        description: 'A federal return was received, accepted, or rejected.',
        alertTypes: ['federal_return_received', 'federal_return_accepted', 'federal_return_rejected'],
      },
      {
        id: 'tax-state-update',
        label: 'State return update',
        description: 'A state return was received, accepted, or rejected.',
        alertTypes: ['state_return_received', 'state_return_accepted', 'state_return_rejected'],
      },
      {
        id: 'tax-filing-ready-review',
        label: 'Tax filing ready for your review',
        description: 'A filing is ready for you to review.',
        alertTypes: ['tax_filing_ready'],
      },
      {
        id: 'tax-enrollment-completed',
        label: 'Client completed tax enrollment',
        description: 'A client finished enrolling in tax service.',
        alertTypes: ['client_completed_tax_enrollment'],
      },
    ],
  },
  {
    category: 'portfolio-accounts',
    title: 'Portfolio & Accounts',
    rows: [
      {
        id: 'transfer-in',
        label: 'Transfer in',
        description: 'When total daily transfers into an account exceed {pct}% of the account value.',
        alertTypes: ['transfer_in'],
        tune: 'transfer',
        group: 'Accounts managed by Savvy',
      },
      {
        id: 'rmds',
        label: 'RMDs due',
        description: 'A client has an outstanding required minimum distribution.',
        alertTypes: ['rmds'],
        group: 'Accounts managed by Savvy',
      },
      {
        id: 'cash-over-threshold',
        label: 'Cash allocation',
        description: 'When cash is more than {pct}% for a specific account.',
        alertTypes: ['cash_variance'],
        tune: 'cash',
        group: 'Accounts managed by Savvy',
      },
      {
        id: 'allocation-drift',
        label: 'Allocation variance',
        description:
          'If fixed income, equities or alternatives fall outside +/- {pct}% of their target for a household.',
        alertTypes: ['allocation_variance'],
        tune: 'allocation',
        group: 'Accounts managed by Savvy',
      },
      {
        id: 'account-funded',
        label: 'Account opened or funded',
        description: 'A new account was opened or funded.',
        alertTypes: ['account_opened_or_funded'],
        group: 'Accounts managed by Savvy',
      },
      {
        id: 'large-withdrawal',
        label: 'Large withdrawal',
        description: 'A large withdrawal or outflow was detected.',
        alertTypes: ['large_withdrawal_detected'],
        group: 'Accounts managed by Savvy',
      },
      {
        id: 'beneficiary-change',
        label: 'Beneficiary or ownership change',
        description: "An account's beneficiary or owner changed.",
        alertTypes: ['beneficiary_or_ownership_change'],
        group: 'Accounts managed by Savvy',
      },
      {
        id: 'account-linked',
        label: 'Account linked',
        description: 'A client linked an outside account through a third party.',
        alertTypes: ['account_linked_or_aggregation_added'],
        group: 'Accounts linked through a third party (non-managed)',
      },
    ],
  },
  {
    category: 'reports',
    title: 'Reports',
    rows: [
      {
        id: 'report-ready',
        label: 'Quarterly report ready',
        description: 'A quarterly report finished generating.',
        alertTypes: ['quarterly_report_generated'],
      },
      {
        id: 'report-failed',
        label: 'Report generation failed',
        description: 'A quarterly report failed to generate.',
        alertTypes: ['quarterly_report_failed'],
      },
    ],
  },
  {
    category: 'meetings',
    title: 'Meetings',
    rows: [
      {
        id: 'meeting-prep-recap-ready',
        label: 'Meeting prep & recap',
        description: 'A meeting prep or recap document finished generating.',
        // One toggle governs both document types: on routes both to the Inbox, off silences both.
        alertTypes: ['meeting_prep_ready', 'meeting_recap_ready'],
        inboxOptional: true,
      },
      {
        id: 'meeting-transcript-needs-household',
        label: 'Meeting transcript needs to be linked',
        description: "A meeting recording came in that we couldn't tie to a household or contact.",
        alertTypes: ['meeting_transcript_needs_household'],
        inboxOnly: true,
      },
    ],
  },
  {
    category: 'client-engagement',
    title: 'Client Engagement',
    rows: [
      {
        id: 'client-birthday',
        label: 'Client birthday',
        description: "On a client's birthday.",
        alertTypes: ['birthdays'],
        tune: 'birthday',
      },
      {
        id: 'client-under-engaged',
        label: 'Client under-engaged',
        description: 'No meaningful contact with a client in {weeks} weeks.',
        alertTypes: ['client_engagement'],
        tune: 'weeks',
      },
      {
        id: 'client-portal-logins',
        label: 'Client portal logins',
        description: 'When a client logs into their portal.',
        alertTypes: ['client_logins'],
      },
      {
        id: 'client-portal-message',
        label: 'Client portal message',
        description: 'A client sent a message through the portal.',
        alertTypes: ['client_portal_message_received'],
      },
      {
        id: 'recurring-investment-reminder',
        label: 'Recurring investment reminder',
        description: "A reminder about a client's recurring (dollar-cost-average) investment.",
        alertTypes: ['recurring_investment_reminder'],
      },
    ],
  },
];
